import { useMutation } from '@platform/api-client';
import { formatList, formatNumber, Select, Tabs, Textarea } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { CSV_FILES, FileDropZone } from '../FileDropZone.tsx';
import { FormSheet } from '../FormSheet.tsx';
import { accountOf, useCarriers } from '../settings/carriers.ts';
import { usePageContext } from '../usePageContext.ts';
import { importBodyOf, type RemittanceDetail, type RemittanceSource } from './finance.ts';
import styles from './Finance.module.css';
import { parsePastedCodes, parseRemittanceCsv, type ParsedRemittance } from './remittance-file.ts';

const MAX_FILE_BYTES = 2_000_000;
const NOTHING_READ: ParsedRemittance = { lines: [], invalidRows: [] };

export type ImportRemittanceSheetProps = {
  api: TypedClient;
  shipping: ShippingClient;
  onClose: () => void;
  onImported: (detail: RemittanceDetail) => void;
};

export const ImportRemittanceSheet = ({ api, shipping, onClose, onImported }: ImportRemittanceSheetProps) => {
  const { t, locale, invalidate, describeError } = usePageContext();
  const carriers = (useCarriers(shipping).data?.items ?? []).filter(
    (carrier) => accountOf(carrier) !== undefined,
  );
  const [carrierCode, setCarrierCode] = useState<string | null>(null);
  const [source, setSource] = useState<RemittanceSource>('paste');
  const [pasted, setPasted] = useState('');
  const [file, setFile] = useState<{ name: string; parsed: ParsedRemittance } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const chosenCarrier = carrierCode ?? carriers[0]?.code ?? '';
  const parsed = source === 'paste' ? parsePastedCodes(pasted) : (file?.parsed ?? NOTHING_READ);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/admin/finance/remittances', { body: importBodyOf(chosenCarrier, source, parsed.lines) }),
    onSuccess: async (detail) => {
      await invalidate('remittances', 'settlements', 'orders');
      onImported(detail);
    },
  });

  const readFile = async (chosen: File) => {
    if (chosen.size > MAX_FILE_BYTES) {
      setProblem(t('remittances.fileTooLarge'));
      return;
    }
    setProblem(null);
    setFile({ name: chosen.name, parsed: parseRemittanceCsv(await chosen.text()) });
  };

  return (
    <FormSheet
      open
      formId="remittance-import"
      title={t('remittances.importTitle')}
      submitLabel={t('remittances.import')}
      submitting={submit.isPending}
      errorMessage={problem ?? (submit.isError ? (describeError(submit.error) ?? null) : null)}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        if (parsed.lines.length === 0) setProblem(t('remittances.nothingRead'));
        else if (chosenCarrier === '') setProblem(t('remittances.noCarrier'));
        else {
          setProblem(null);
          submit.mutate();
        }
      }}
    >
      <div className={styles['stack']}>
        <Select
          label={t('remittances.carrier')}
          required
          value={chosenCarrier}
          options={carriers.map((carrier) => ({ value: carrier.code, label: carrier.name }))}
          onChange={(event) => {
            setCarrierCode(event.target.value);
          }}
        />
        <Tabs
          activeId={source}
          onChange={(id) => {
            setSource(id === 'csv' ? 'csv' : 'paste');
          }}
          tabs={[
            {
              id: 'paste',
              label: t('remittances.sourcePaste'),
              content: (
                <Textarea
                  label={t('remittances.codes')}
                  helperText={t('remittances.codesHelp')}
                  rows={8}
                  spellCheck={false}
                  value={pasted}
                  onChange={(event) => {
                    setPasted(event.target.value);
                  }}
                />
              ),
            },
            {
              id: 'csv',
              label: t('remittances.sourceCsv'),
              content: (
                <FileDropZone
                  accept={CSV_FILES}
                  label={file?.name ?? t('remittances.drop')}
                  helperText={t('remittances.dropHelp')}
                  onFile={(chosen) => {
                    void readFile(chosen);
                  }}
                />
              ),
            },
          ]}
        />
        <p className={styles['muted']} aria-live="polite">
          {t('remittances.preview', { count: formatNumber(parsed.lines.length, locale) })}
        </p>
        {parsed.invalidRows.length === 0 ? null : (
          <p className={styles['warning']} role="status">
            {t('remittances.invalidRows', {
              rows: formatList(parsed.invalidRows.map(String), locale),
            })}
          </p>
        )}
      </div>
    </FormSheet>
  );
};
